import { useRef, useState } from 'react';
import { MAX_NOTE_LENGTH, isReflectionColumn, type ColumnId, type PublicRetroNote } from '@planning-session/shared';
import { SortIcon } from '../ui/Icon.js';
import { NoteCard } from './NoteCard.js';
import { NoteComposer } from './NoteComposer.js';
import styles from './Column.module.css';

export interface ColumnProps {
  readonly heading: string;
  readonly accent: 'green' | 'amber' | 'indigo';
  readonly column: ColumnId;
  readonly columnIndex: number;
  readonly notes: readonly PublicRetroNote[];
  readonly phase: 'writing' | 'revealed';
  readonly concealing: boolean;
  readonly locked: boolean;
  readonly isPlayer: boolean;
  readonly isHost: boolean;
  readonly viewerId: string | null;
  onAddNote: (text: string) => void;
  onEditNote: (noteId: string, text: string) => void;
  onRemoveNote: (noteId: string) => void;
  onVoteNote: (noteId: string) => void;
  onUnvoteNote: (noteId: string) => void;
  onCombineNotes: (noteId: string, intoNoteId: string) => void;
  onUnmergeNote: (noteId: string) => void;
}

// The reveal reads as one board-wide wave, not N notes updating independently:
// concealment lifts column by column, top to bottom within each. See DESIGN.md
// on the planning table's dealer stagger — this is the retro board's version of
// the same idea, expressed as a fade rather than a flip (the flip stays the
// planning table's alone).
const COLUMN_WAVE_MS = 70;
const NOTE_WAVE_MS = 45;

export function Column({ heading, accent, column, columnIndex, notes, phase, concealing, locked, isPlayer, isHost, viewerId, onAddNote, onEditNote, onRemoveNote, onVoteNote, onUnvoteNote, onCombineNotes, onUnmergeNote }: ColumnProps) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [sortByVotes, setSortByVotes] = useState(false);
  const reflection = isReflectionColumn(column);
  const canVote = phase === 'revealed' && reflection && isPlayer;
  const canMerge = phase === 'revealed' && reflection && isHost;
  // Votes only exist post-reveal, on reflection columns — nothing to sort by
  // anywhere else, or before there is anything to sort.
  const canSort = phase === 'revealed' && reflection;

  // Reveal is one-way for a retro's whole lifetime (see domain.ts — there is
  // no un-reveal), so the notes' reveal wave only ever needs to trigger once.
  // `revealSeq` goes 0 -> 1 on that transition and then holds, giving `key`s
  // downstream exactly one change to remount on — never re-triggering on a
  // later, unrelated re-render (a vote, a presence change) while still
  // revealed, the way a plain "did the transition just happen" boolean would:
  // that boolean is true for one render and false for every render after,
  // which is two changes, not one.
  const prevLocked = useRef(locked);
  const revealSeq = useRef(0);
  if (phase === 'revealed') revealSeq.current = 1;
  const justUnlocked = prevLocked.current && !locked;
  prevLocked.current = locked;

  // Delay is assigned by arrival order — the position notes revealed in — so
  // sorting by votes afterward reorders the display without reshuffling or
  // replaying the wave: each note keeps the delay it already animated in with.
  const withDelay = notes.map((note, noteIndex) => ({
    note,
    delayMs: columnIndex * COLUMN_WAVE_MS + noteIndex * NOTE_WAVE_MS,
  }));
  const displayNotes =
    sortByVotes && canSort ? [...withDelay].sort((a, b) => (b.note.voteCount ?? 0) - (a.note.voteCount ?? 0)) : withDelay;

  return (
    <section className={styles.column} aria-label={heading}>
      <header className={styles.head}>
        <span className={`${styles.dot} ${styles[accent]}`} aria-hidden="true" />
        <h2 className={styles.heading}>{heading}</h2>
        <span className={styles.count}>{notes.length}</span>
        {canSort && (
          <button
            type="button"
            className={`${styles.sortButton} ${sortByVotes ? styles.sortActive : ''}`}
            aria-pressed={sortByVotes}
            aria-label={sortByVotes ? 'Showing highest votes first' : 'Sort by votes'}
            title={sortByVotes ? 'Showing highest votes first' : 'Sort by votes'}
            onClick={() => setSortByVotes((value) => !value)}
          >
            <SortIcon size={16} />
          </button>
        )}
      </header>

      <div className={styles.notes}>
        {displayNotes.map(({ note, delayMs }) => (
          <NoteCard
            key={note.id}
            note={note}
            phase={phase}
            concealing={concealing}
            revealSeq={revealSeq.current}
            revealDelayMs={delayMs}
            canVote={canVote}
            canMerge={canMerge}
            isAuthor={!note.merged && note.mine}
            canRemove={
              note.merged
                ? isHost
                : (note.mine && (!reflection || phase === 'writing')) || (isHost && !reflection && phase === 'revealed')
            }
            canUnmerge={isHost && note.merged}
            draggable={canMerge && note.fragments !== null}
            onEdit={(text) => onEditNote(note.id, text)}
            onRemove={() => onRemoveNote(note.id)}
            onUnmerge={() => onUnmergeNote(note.id)}
            onVote={() => onVoteNote(note.id)}
            onUnvote={() => onUnvoteNote(note.id)}
            onDragStart={() => setDragId(note.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => {
              if (dragId && dragId !== note.id) onCombineNotes(dragId, note.id);
              setDragId(null);
            }}
          />
        ))}
      </div>

      {(locked || isPlayer) && (
        <div key={locked ? 'locked' : 'open'} className={`${styles.foot} ${justUnlocked ? styles.opening : ''}`}>
          {locked ? (
            <p className={styles.lockedNotice}>{phase === 'writing' ? 'Opens once the board is revealed.' : 'No longer taking notes.'}</p>
          ) : (
            <NoteComposer maxLength={MAX_NOTE_LENGTH} disabled={false} onSubmit={onAddNote} />
          )}
        </div>
      )}
    </section>
  );
}
